// frontend/src/api/billing.js
/**
 * Billing API — thin wrappers around the /api/billing and /api/referrals endpoints.
 */
import client from "./client";

/**
 * Return the active org's current subscription status.
 * @returns {Promise<{plan_name: string, status: string, current_period_end: string|null, trial_end: string|null}>}
 */
export async function getBillingStatus() {
  const r = await client.get("/api/billing/status");
  return r.data;
}

/**
 * Return up to 10 of the org's most recent Stripe invoices.
 * @returns {Promise<Array<{id: string, amount_due: number, status: string|null, created: number, hosted_invoice_url: string|null}>>}
 */
export async function getInvoices() {
  const r = await client.get("/api/billing/invoices");
  return r.data;
}

/**
 * Create a Stripe Checkout session and return the hosted URL.
 * Owner-only endpoint; the caller is responsible for redirecting.
 * QRFlow sells in EUR only.
 *
 * @param {string} planName      Case-insensitive plan name, e.g. "pro".
 * @param {"monthly"|"annual"} billingPeriod
 * @returns {Promise<string>} The Checkout session URL.
 */
export async function createCheckout(planName, billingPeriod = "monthly") {
  const r = await client.post("/api/billing/checkout", {
    plan_name: planName,
    billing_period: billingPeriod,
  });
  return r.data.checkout_url;
}

/**
 * Create a Stripe Billing Portal session and return the hosted URL.
 * Owner-only endpoint.
 *
 * @returns {Promise<string>} The Billing Portal session URL.
 */
export async function createPortal() {
  const r = await client.post("/api/billing/portal");
  return r.data.portal_url;
}

/**
 * Estimate the all-in price + tax for a plan/period/country (no side effects).
 * QRFlow sells in EUR only.
 *
 * @param {string} planName
 * @param {"monthly"|"annual"} billingPeriod
 * @param {string} countryCode  ISO 3166-1 alpha-2, e.g. "DE".
 * @param {string|null} postalCode  Required for US/CA sub-national tax.
 * @returns {Promise<{subtotal: number, tax_amount: number, tax_rate: number, total: number, currency: string}>}
 */
export async function estimateTax(
  planName,
  billingPeriod,
  countryCode,
  postalCode = null,
) {
  const payload = {
    plan_name: planName,
    billing_period: billingPeriod,
    country_code: countryCode,
  };
  if (postalCode) payload.postal_code = postalCode;
  const r = await client.post("/api/billing/estimate", payload);
  return r.data;
}

/**
 * Return the active org's referral-programme statistics.
 * @returns {Promise<{referral_link: string, total_referred: number, converted: number, pending: number, rewards_earned: number}>}
 */
export async function getReferralStats() {
  const r = await client.get("/api/referrals/stats");
  return r.data;
}

/**
 * Preview a voucher code (public — no side effects). Rejects (404) if the code
 * is unknown, inactive, expired, or exhausted.
 *
 * @param {string} code  The voucher code.
 * @returns {Promise<{code: string, discount_type: string, discount_value: number, description: string, valid_until: string|null, applicable_plans: string[]|null}>}
 */
export async function getVoucherPreview(code) {
  const r = await client.get(`/api/billing/voucher/${encodeURIComponent(code)}`);
  return r.data;
}

/**
 * Redeem a voucher for the active org. Owner-only endpoint.
 *
 * @param {string} code            The voucher code to redeem.
 * @param {string|null} planName   Plan being purchased, for the plan check.
 * @returns {Promise<{code: string, discount_type: string, discount_applied: number, description: string, white_account: boolean, redeemed_at: string}>}
 */
export async function redeemVoucher(code, planName = null) {
  const payload = { code };
  if (planName) payload.plan_name = planName;
  const r = await client.post("/api/billing/redeem-voucher", payload);
  return r.data;
}
